import React, { useState } from 'react';
import './ExportReport.less';
import { getProtectionTypeName, getCalculationFormula, formatResultLabel, mapCurveLabel, formatResultValueExport } from '../utils/exportUtils';
import { getParamLabel } from '../utils/labelMappings';
import { getFieldRange, isFieldApplicable } from '../utils/validationRules';
import { ZPlaneMini, IdIrMini, BarChartMini } from './MiniCharts';
import { useI18n } from '../i18n/i18n.jsx';

const asText = (v) => {
  if (v === null || v === undefined) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};

const formatResultValue = (key, value) => formatResultValueExport(key, value);

const buildParamItems = (p = {}, type = 'unknown', t) => {
  const items = [];
  if (isFieldApplicable(type, 'systemVoltage') && p.systemVoltage != null) items.push([getParamLabel('systemVoltage', type), `${p.systemVoltage} kV`]);
  if (isFieldApplicable(type, 'ratedCurrent') && p.ratedCurrent != null) items.push([getParamLabel('ratedCurrent', type), `${p.ratedCurrent} A`]);
  if (isFieldApplicable(type, 'ctRatio') && p.ctRatio != null) items.push([getParamLabel('ctRatio', type), `1:${p.ctRatio}`]);
  if (isFieldApplicable(type, 'ptRatio') && p.ptRatio != null) items.push([getParamLabel('ptRatio', type), `1:${p.ptRatio}`]);
  if (isFieldApplicable(type, 'faultCurrent') && p.faultCurrent != null) items.push([getParamLabel('faultCurrent', type), `${p.faultCurrent} A`]);
  if (isFieldApplicable(type, 'transformerCapacity') && p.transformerCapacity != null) items.push([getParamLabel('transformerCapacity', type), `${p.transformerCapacity} kVA`]);
  items.push([getParamLabel('protectionType', type), getProtectionTypeName(type, { t })]);
  if (['overcurrent','directional','zero_sequence'].includes(type)) {
    items.push([getParamLabel('curveType', type), mapCurveLabel(p.curveType || 'Normal Inverse')]);
    if (p.timeMultiplier != null && isFieldApplicable(type, 'timeMultiplier')) items.push([getParamLabel('timeMultiplier', type), `${p.timeMultiplier}`]);
  }
  if (type === 'directional' && isFieldApplicable(type, 'polarizingAngle') && p.polarizingAngle != null) items.push([getParamLabel('polarizingAngle', type), `${p.polarizingAngle}`]);
  if (type === 'zero_sequence' && isFieldApplicable(type, 'k0') && p.k0 != null) items.push([getParamLabel('k0', type), `${p.k0}`]);
  return items;
};

// 参数范围说明（依赖统一校验模块）
const FIELD_UNIT_MAP = {
  systemVoltage: 'kV',
  ratedCurrent: 'A',
  ctRatio: '',
  ptRatio: '',
  faultCurrent: 'A',
  transformerCapacity: 'kVA',
  timeMultiplier: '',
  polarizingAngle: '°',
  k0: '',
  lineLength_km: 'km',
  lineR_per_km_ohm: 'Ω/km',
  lineX_per_km_ohm: 'Ω/km',
  lineR_ohm: 'Ω',
  lineX_ohm: 'Ω',
  lineAngleDeg: '°'
};

const buildParamNotes = (p = {}, type = 'unknown') => {
  const items = [];
  Object.keys(FIELD_UNIT_MAP).forEach((field) => {
    const rng = getFieldRange(type, field);
    if (!rng) return;
    // 仅在该参数存在或适用于类型时展示
    if (p[field] === undefined && !Array.isArray(rng)) return;
    const unit = FIELD_UNIT_MAP[field] || '';
    const label = getParamLabel(field, type);
    items.push([label, `${rng[0]}–${rng[1]}${unit ? ` ${unit}` : ''}`]);
  });
  return items;
};

const SUMMARY_KEYS_BY_TYPE = {
  overcurrent: ['pickupCurrent', 'timeMultiplier', 'operateTime'],
  directional: ['polarizingAngle', 'pickupCurrent'],
  zero_sequence: ['zeroSequenceCurrent', 'pickupCurrent'],
  distance: ['zone1Setting', 'zone2Setting', 'zone3Setting'],
  differential: ['restraintSlope', 'diffInstantaneousSecondary', 'differentialCurrentSetting', 'secondHarmonicBlocking'],
  longitudinal: ['differentialCurrent', 'pickupCurrent', 'operateTime']
};

const buildSummaryItems = (type, r = {}) => {
  const keys = SUMMARY_KEYS_BY_TYPE[type] || [];
  return keys
    .filter((k) => r[k] != null)
    .map((k) => [formatResultLabel(k, type), formatResultValueExport(k, r[k])]);
};

const CurveChartMini = ({ points = [] }) => {
  const width = 720;
  const height = 360;
  const padding = { top: 10, right: 20, bottom: 24, left: 28 };
  if (!Array.isArray(points) || points.length === 0) {
    return <div style={{ color: '#999' }}>无曲线数据</div>;
  }

  const Mmax = Math.max(...points.map(p => p.M || 0));
  const Tmax = Math.max(...points.map(p => (p.t ?? p.T) || 0));
  const xScale = (M) => padding.left + (M / Mmax) * (width - padding.left - padding.right);
  const yScale = (T) => (height - padding.bottom) - (T / Tmax) * (height - padding.top - padding.bottom);

  const pathD = points
    .sort((a, b) => a.M - b.M)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.M)} ${yScale(p.t ?? p.T)}`)
    .join(' ');

  const xTicks = Array.from({ length: 6 }).map((_, i) => (i * Mmax / 5));
  const yTicks = Array.from({ length: 6 }).map((_, i) => (i * Tmax / 5));

  return (
    <svg className="h-curve-svg" width={width} height={height}>
      <rect x={0} y={0} width={width} height={height} fill="#fff" />
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#999" strokeWidth={1} />
      <path d={pathD} stroke="#1677ff" strokeWidth={2} fill="none" />
      {xTicks.map((M, i) => {
        const x = xScale(M);
        return (
          <g key={i}>
            <line x1={x} y1={height - padding.bottom} x2={x} y2={height - padding.bottom - 5} stroke="#aaa" />
            <text x={x - 10} y={height - padding.bottom + 16} fontSize="12" fill="#666">{M.toFixed(1)}</text>
          </g>
        );
      })}
      {yTicks.map((t, i) => {
        const y = yScale(t);
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={padding.left + 5} y2={y} stroke="#aaa" />
            <text x={8} y={y + 4} fontSize="12" fill="#666">{t.toFixed(1)}s</text>
          </g>
        );
      })}
    </svg>
  );
};

// 采用复用的迷你图组件（ZPlaneMini、IdIrMini、BarChartMini）

const ExportReport = ({ name = '', type = 'unknown', parameters = {}, results = {}, remark = '', extras = {} }) => {
  const { t } = useI18n();
  const typeName = getProtectionTypeName(type, { t });
  const formulas = getCalculationFormula(type)?.formulas || [];
  const summaryItems = buildSummaryItems(type, results);
  const hasTags = Array.isArray(extras.tags) && extras.tags.length > 0;
  const theme = extras?.theme || {};
  const engineeringWarnings = Array.isArray(extras.engineeringWarnings) ? extras.engineeringWarnings : [];
  const deviceWarnings = Array.isArray(extras.deviceWarnings) ? extras.deviceWarnings : [];
  const deviceBlockers = Array.isArray(extras.deviceBlockers) ? extras.deviceBlockers : [];
  const [warningFilter, setWarningFilter] = useState('all');
  const [includeProjectLine, setIncludeProjectLine] = useState(false);
  const [showTuningLog, setShowTuningLog] = useState(extras?.includeAutoTuningLog !== false);
  const [tuningFilter, setTuningFilter] = useState('all'); // all | apply | rollback
  const selectivityMatrix = extras?.selectivityMatrix;
  const hasSelectivity = selectivityMatrix && Array.isArray(selectivityMatrix.pairs) && selectivityMatrix.pairs.length > 0;
  const selectivityPanel = extras?.selectivityPanel;
  const overlay = extras?.overlay;
  const hasOverlay = !!(overlay && Array.isArray(overlay.defs) && overlay.defs.length > 0);
  const deviceRefs = extras?.deviceRefs;
  const deviceDetails = extras?.deviceDetails;
  const includeParamNotes = extras?.includeParamNotes !== false;
  const includeDeviceSource = extras?.includeDeviceSource !== false;
  const tuningEvents = Array.isArray(extras?.autoTuningLogEvents) ? extras.autoTuningLogEvents : [];
  const tuningSummary = (extras && extras.autoTuningParamSummary);
  const filteredTuningEvents = tuningEvents.filter((evt) => {
    if (tuningFilter === 'apply') return evt?.action === 'apply';
    if (tuningFilter === 'rollback') return evt?.action === 'rollback';
    return true;
  });
  const fmtParamValue = (key, value) => {
    if (value == null) return '—';
    if (key === 'curveType') return mapCurveLabel(value);
    if (typeof value === 'number' && isFinite(value)) {
      if (/timeMultiplier|tms/i.test(String(key))) return Number(value).toFixed(3);
      return String(value);
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };
  const includeSelectivity = extras?.includeSelectivity !== false;

  // 组装条形图数据（选取前5个数值型结果）
  const barData = Object.entries(results || {})
    .filter(([k, v]) => typeof v === 'number' && isFinite(v))
    .slice(0, 5)
    .map(([k, v]) => ({ label: formatResultLabel(k, type), value: v }));

  // 目录：存在的段落
  const paramNotes = buildParamNotes(parameters, type);
  const toc = [
    summaryItems.length > 0 ? '摘要' : null,
    '上下文',
    (tuningSummary && Array.isArray(tuningSummary.changedParams) && tuningSummary.changedParams.length > 0) ? '自动整定参数摘要' : null,
    (showTuningLog && filteredTuningEvents.length > 0) ? '自动整定变更日志' : null,
    '输入参数',
    '计算结果',
    hasOverlay ? '叠加设置' : null,
    ((selectivityPanel && selectivityPanel.enabled && Array.isArray(selectivityPanel.seriesOrder) && selectivityPanel.seriesOrder.length > 1) ? '选择性面板配置' : null),
    (includeSelectivity && hasSelectivity) ? '选择性校核' : null,
    (deviceWarnings.length > 0 || deviceBlockers.length > 0) ? '设备校核' : null,
    (results?.curvePoints && Array.isArray(results.curvePoints) && results.curvePoints.length > 0) ? '反时限曲线' : null,
    (type === 'distance' && Array.isArray(results?.zones) && results.zones.length > 0) ? '阻抗平面' : null,
    (type === 'differential' && results?.characteristic) ? '差动特性' : null,
    (barData.length > 0) ? '条形图' : null,
    extras.comments && extras.comments.trim() ? '批注' : null,
    engineeringWarnings.length > 0 ? '工程校核提示' : null,
    '计算公式',
    (includeParamNotes && paramNotes.length > 0) ? '参数说明' : null,
  ].filter(Boolean);

  return (
    <div className="export-report">
      {(theme.showCover || theme.logoUrl) && (
        <div className="report-cover">
          {theme.logoUrl ? <img className="cover-logo" src={theme.logoUrl} alt="logo" /> : null}
          <div className="cover-title">{theme.coverTitle || (name || '未命名')}</div>
          <div className="cover-sub">{theme.coverSubtitle || typeName}</div>
          {theme.companyName ? <div className="cover-company">{theme.companyName}</div> : null}
          <div className="cover-date">{new Date().toLocaleString('zh-CN')}</div>
        </div>
      )}

      {toc.length > 0 && (
        <div className="h-section report-toc">
          <div className="h-title">目录</div>
          <ol className="toc-list">
            {toc.map((t, idx) => (<li key={idx}>{t}</li>))}
          </ol>
        </div>
      )}

      <div className="h-section h-summary">
        <div className="h-title">名称：{name || '未命名'}</div>
        <div className="h-sub">保护类型：{typeName}</div>
        {remark ? <div className="h-remark">备注：{remark}</div> : null}
      </div>

      {summaryItems.length > 0 && (
        <div className="h-section">
          <div className="h-title">摘要</div>
          <ul className="h-list">
            {summaryItems.map(([label, val], idx) => (
              <li key={idx}><strong>{label}</strong>: {val}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="h-section">
        <div className="h-title">上下文</div>
        <ul className="h-list">
          <li><strong>导出时间</strong>: {new Date().toLocaleString('zh-CN')}</li>
          {extras.project ? (<li><strong>项目</strong>: {extras.project}</li>) : null}
          {extras.line ? (<li><strong>线路</strong>: {extras.line}</li>) : null}
          {hasTags ? (<li><strong>标签</strong>: {extras.tags.join(', ')}</li>) : null}
          {extras.versionStamp ? (<li><strong>版本戳</strong>: {extras.versionStamp}</li>) : null}
        </ul>
      </div>

      {showTuningLog && filteredTuningEvents.length > 0 && (
        <div className="h-section">
          <div className="h-title">自动整定变更日志</div>
          <div className="print-hide" style={{ marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={showTuningLog} onChange={(e) => setShowTuningLog(e.target.checked)} />
              显示此段
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>筛选</span>
              <select value={tuningFilter} onChange={(e) => setTuningFilter(e.target.value)}>
                <option value="all">全部</option>
                <option value="apply">仅应用</option>
                <option value="rollback">仅回滚</option>
              </select>
            </label>
            <span style={{ color: '#666' }}>事件数：{filteredTuningEvents.length}</span>
          </div>
          {filteredTuningEvents.map((evt, idx) => {
            const actionText = evt?.action === 'apply' ? '应用建议' : (evt?.action === 'rollback' ? '回滚至快照' : String(evt?.action || '事件'));
            let timeText = '';
            try { timeText = new Date(evt?.time).toLocaleString('zh-CN'); } catch (_) { timeText = String(evt?.time || ''); }
            const diffs = Array.isArray(evt?.changes) && evt.changes.length > 0 ? evt.changes : (Array.isArray(evt?.reverted) ? evt.reverted : []);
            return (
              <div key={`tun-${idx}`} style={{ marginBottom: 8 }}>
                <div className="h-sub">事件 {idx + 1}：{actionText}（{timeText}）</div>
                <ul className="h-list">
                  {evt?.source ? (<li><strong>来源</strong>: {String(evt.source)}</li>) : null}
                  {evt?.type ? (<li><strong>保护类型</strong>: {getProtectionTypeName(evt.type, { t })}</li>) : null}
                  {evt?.goal ? (<li><strong>目标</strong>: {evt.goal}</li>) : null}
                  {(Array.isArray(evt?.appliedFields) && evt.appliedFields.length > 0) ? (
                    <li><strong>应用字段</strong>: {evt.appliedFields.join(', ')}</li>
                  ) : null}
                  {(Array.isArray(evt?.rationale) && evt.rationale.length > 0) ? (
                    <li>
                      <strong>理由</strong>:
                      <ul className="h-list" style={{ marginLeft: 16 }}>
                        {evt.rationale.map((r, i) => (<li key={`rat-${idx}-${i}`}>{r}</li>))}
                      </ul>
                    </li>
                  ) : null}
                  {(Array.isArray(evt?.warnings) && evt.warnings.length > 0) ? (
                    <li>
                      <strong>警告</strong>:
                      <ul className="h-list" style={{ marginLeft: 16 }}>
                        {evt.warnings.map((w, i) => (<li key={`warn-${idx}-${i}`}>⚠️ {w}</li>))}
                      </ul>
                    </li>
                  ) : null}
                  {(evt?.userAdjustments && typeof evt.userAdjustments === 'object' && Object.keys(evt.userAdjustments).length > 0) ? (
                    <li>
                      <strong>用户后续调整</strong>:
                      <ul className="h-list" style={{ marginLeft: 16 }}>
                        {Object.entries(evt.userAdjustments).map(([k, v]) => (
                          <li key={`ua-${idx}-${k}`}><strong>{getParamLabel(k, type)}</strong>: {fmtParamValue(k, v)}</li>
                        ))}
                      </ul>
                    </li>
                  ) : null}
                  {diffs.length > 0 ? (
                    <li>
                      <strong>参数差异</strong>:
                      <ul className="h-list" style={{ marginLeft: 16 }}>
                        {diffs.map((c, i) => (
                          <li key={`diff-${idx}-${i}`}><strong>{c?.label || getParamLabel(c?.key, type)}</strong>: {fmtParamValue(c?.key, c?.from)} → {fmtParamValue(c?.key, c?.to)}</li>
                        ))}
                      </ul>
                    </li>
                  ) : null}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {(tuningSummary && Array.isArray(tuningSummary.changedParams) && tuningSummary.changedParams.length > 0) && (
        <div className="h-section">
          <div className="h-title">自动整定参数摘要</div>
          <ul className="h-list">
            {typeof tuningSummary.applyCount === 'number' ? (<li><strong>应用次数</strong>: {tuningSummary.applyCount}</li>) : null}
            {typeof tuningSummary.rollbackCount === 'number' ? (<li><strong>回滚次数</strong>: {tuningSummary.rollbackCount}</li>) : null}
            {tuningSummary.lastAction ? (
              <li><strong>最近操作</strong>: {tuningSummary.lastAction === 'apply' ? '应用建议' : (tuningSummary.lastAction === 'rollback' ? '回滚至快照' : tuningSummary.lastAction)}{tuningSummary.lastTime ? `（${new Date(tuningSummary.lastTime).toLocaleString('zh-CN')}）` : ''}</li>
            ) : null}
            {(Array.isArray(tuningSummary.rationale) && tuningSummary.rationale.length > 0) ? (
              <li>
                <strong>理由</strong>:
                <ul className="h-list" style={{ marginLeft: 16 }}>
                  {tuningSummary.rationale.map((r, i) => (<li key={`sum-rat-${i}`}>{r}</li>))}
                </ul>
              </li>
            ) : null}
            {(Array.isArray(tuningSummary.warnings) && tuningSummary.warnings.length > 0) ? (
              <li>
                <strong>警告</strong>:
                <ul className="h-list" style={{ marginLeft: 16 }}>
                  {tuningSummary.warnings.map((w, i) => (<li key={`sum-warn-${i}`}>⚠️ {w}</li>))}
                </ul>
              </li>
            ) : null}
            <li>
              <strong>汇总参数</strong>:
              <ul className="h-list" style={{ marginLeft: 16 }}>
                {tuningSummary.changedParams.map((c, i) => (
                  <li key={`sum-param-${i}`}><strong>{c?.label || getParamLabel(c?.key, type)}</strong>: {formatResultValue(c?.key, c?.to)}</li>
                ))}
              </ul>
            </li>
          </ul>
        </div>
      )}

      {(includeDeviceSource && (deviceRefs || deviceDetails)) && (
        <div className="h-section">
          <div className="h-title">设备来源</div>
          <ul className="h-list">
            {deviceDetails ? (
              <>
                <li><strong>馈线</strong>: {(deviceDetails.feeder?.name || deviceDetails.feeder?.id || '未知')} {deviceDetails.feeder?.id ? `(${deviceDetails.feeder.id})` : ''}</li>
                {deviceDetails.bus?.voltage_kV != null ? (<li><strong>母线电压</strong>: {deviceDetails.bus.voltage_kV} kV</li>) : null}
                {deviceDetails.ratedCurrent != null ? (<li><strong>额定电流</strong>: {deviceDetails.ratedCurrent} A</li>) : null}
                {deviceDetails.ctRatio != null ? (<li><strong>CT变比</strong>: 1:{deviceDetails.ctRatio}</li>) : null}
                {deviceDetails.source ? (
                  <li><strong>电源短路容量</strong>: {deviceDetails.source.S_sc_MVA} MVA；X/R={deviceDetails.source.X_R}</li>
                ) : null}
                {deviceDetails.transformer ? (
                  <li><strong>变压器</strong>: {deviceDetails.transformer.S_tr_MVA} MVA；uk={deviceDetails.transformer.uk_percent}%；组别={deviceDetails.transformer.vector_group}{deviceDetails.transformer.secondary_neutral_ground ? `；中性点接地=${deviceDetails.transformer.secondary_neutral_ground}` : ''}</li>
                ) : null}
                {deviceDetails.line ? (
                  <li><strong>线路</strong>: 长度={deviceDetails.line.length_km} km；R={deviceDetails.line.R_per_km_ohm} Ω/km；X={deviceDetails.line.X_per_km_ohm} Ω/km{deviceDetails.line.z0_factor != null ? `；零序系数=${deviceDetails.line.z0_factor}` : ''}</li>
                ) : null}
                {deviceDetails.ground ? (
                  <li><strong>接地阻抗</strong>: R={deviceDetails.ground.R_ohm} Ω；X={deviceDetails.ground.X_ohm} Ω</li>
                ) : null}
              </>
            ) : null}
            {!deviceDetails && deviceRefs ? (
              <>
                {deviceRefs.feeder_id ? (<li><strong>馈线ID</strong>: {deviceRefs.feeder_id}</li>) : null}
                {deviceRefs.ct_id ? (<li><strong>CT ID</strong>: {deviceRefs.ct_id}</li>) : null}
                {deviceRefs.pt_id ? (<li><strong>PT ID</strong>: {deviceRefs.pt_id}</li>) : null}
                {deviceRefs.relay_id ? (<li><strong>继电器ID</strong>: {deviceRefs.relay_id}</li>) : null}
              </>
            ) : null}
          </ul>
        </div>
      )}

      <div className="h-section">
        <div className="h-title">输入参数</div>
        <ul className="h-list">
          {buildParamItems(parameters, type, t).map(([label, val], idx) => (
            <li key={idx}><strong>{label}</strong>: {val}</li>
          ))}
        </ul>
      </div>

      {(() => {
        const notes = paramNotes;
        return (includeParamNotes && notes.length > 0) ? (
          <div className="h-section">
            <div className="h-title">参数说明</div>
            <ul className="h-list">
              {notes.map(([label, text], idx) => (
                <li key={`note-${idx}`}><strong>{label}</strong>: {text}</li>
              ))}
            </ul>
          </div>
        ) : null;
      })()}

      <div className="h-section">
        <div className="h-title">计算结果</div>
        <ul className="h-list">
          {Object.entries(results || {})
            .filter(([k]) => k !== 'curvePoints' && k !== 'characteristic')
            .map(([k, v]) => (
              <li key={k}><strong>{formatResultLabel(k, type)}</strong>: {formatResultValue(k, v)}</li>
            ))}
        </ul>
      </div>

      {hasOverlay && (() => {
        const defs = Array.isArray(overlay?.defs) ? overlay.defs : [];
        const settings = overlay?.settings || {};
        const panelEnabled = !!(selectivityPanel && selectivityPanel.enabled && Array.isArray(selectivityPanel.seriesOrder) && selectivityPanel.seriesOrder.length > 1);
        const sortText = panelEnabled ? `${t('export.overlay.sort.by_panel_selection_prefix')}（${(selectivityPanel.seriesOrder || []).join(' → ')}）` : t('export.overlay.sort.by_current_view');
        const globalMin = (typeof overlay?.minMargin === 'number' ? overlay.minMargin : 0.3);
        const perPair = Array.isArray(overlay?.minMargins) ? overlay.minMargins : [];
        return (
          <div className="h-section">
            <div className="h-title">叠加设置</div>
            <ul className="h-list">
              <li><strong>排序方式</strong>: {sortText}</li>
              <li><strong>最小裕度（全局）</strong>: {globalMin}s</li>
              {overlay?.filenamePrefixResolved ? (
                <li><strong>导出前缀（解析后）</strong>: {overlay.filenamePrefixResolved}</li>
              ) : null}
              {perPair.length > 0 && panelEnabled ? (
                <li>
                  <strong>逐对最小裕度（覆盖项）</strong>:
                  <ul className="h-list" style={{ marginTop: 4 }}>
                    {(selectivityPanel.seriesOrder || []).slice(0, -1).map((dn, idx) => {
                      const up = (selectivityPanel.seriesOrder || [])[idx + 1];
                      const mv = perPair[idx];
                      return (
                        <li key={`ov-mm-${idx}`}><strong>{dn}</strong> → <strong>{up}</strong>: {mv == null ? `使用全局 ${globalMin}s` : `${mv}s`}</li>
                      );
                    })}
                  </ul>
                </li>
              ) : null}
              {(typeof settings.zoomX === 'number' || typeof settings.zoomY === 'number') ? (
                <li><strong>视图</strong>: 缩放X={settings.zoomX ?? 1}，缩放Y={settings.zoomY ?? 1}；平移X={settings.panX ?? 0}，平移Y={settings.panY ?? 0}</li>
              ) : null}
              {Array.isArray(settings.refLineKeys) && settings.refLineKeys.length > 0 ? (
                <li><strong>参考线</strong>: {settings.refLineKeys.join(', ')}</li>
              ) : null}
            </ul>
            {defs.length > 0 && (
              <div style={{ overflowX: 'auto', marginTop: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'left', padding: '6px 8px' }}>名称</th>
                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'center', padding: '6px 8px' }}>坐标系</th>
                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '6px 8px' }}>pickup(A)</th>
                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '6px 8px' }}>TMS</th>
                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'left', padding: '6px 8px' }}>曲线/特性</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defs.map((d, idx) => (
                      <tr key={`ov-def-${idx}`}>
                        <td style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 8px' }}>{d.label}</td>
                        <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'center', padding: '6px 8px' }}>{d.axesType || 'I-t'}</td>
                        <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '6px 8px' }}>{d.pickup == null ? '—' : String(d.pickup)}</td>
                        <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '6px 8px' }}>{d.TMS == null ? '—' : String(d.TMS)}</td>
                        <td style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 8px' }}>{d.curveType || (d.characteristic ? '差动特性' : (Array.isArray(d.zones) && d.zones.length > 0 ? '阻抗平面' : '—'))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {(selectivityPanel && selectivityPanel.enabled && Array.isArray(selectivityPanel.seriesOrder) && selectivityPanel.seriesOrder.length > 1) && (
        <div className="h-section">
          <div className="h-title">选择性面板配置</div>
          <div className="h-sub" style={{ marginTop: 4, color: '#666' }}>串联顺序：{(selectivityPanel.seriesOrder || []).join(' → ')}</div>
          <div className="h-sub" style={{ color: '#666' }}>全局最小裕度：{selectivityPanel.globalMinMargin ?? 0.3}s</div>
          {Array.isArray(selectivityPanel.minMargins) && selectivityPanel.minMargins.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className="h-sub" style={{ color: '#666' }}>逐对最小裕度（覆盖项）</div>
              <ul className="h-list">
                {(selectivityPanel.seriesOrder || []).slice(0, -1).map((dn, idx) => {
                  const up = (selectivityPanel.seriesOrder || [])[idx + 1];
                  const mv = selectivityPanel.minMargins[idx];
                  const global = selectivityPanel.globalMinMargin ?? 0.3;
                  return (
                    <li key={`mm-${idx}`}>
                      <strong>{dn}</strong> → <strong>{up}</strong>: {mv == null ? `使用全局 ${global}s` : `${mv}s`}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {(deviceWarnings.length > 0 || deviceBlockers.length > 0) && (
        <div className="h-section">
          <div className="h-title">设备校核</div>
          {deviceBlockers.length > 0 ? (
            <>
              <div className="h-sub" style={{ color: '#b91c1c', marginTop: 4 }}>阻断项：请修正后再计算</div>
              <ul className="h-list">
                {deviceBlockers.map((b, idx) => (<li key={`dev-block-${idx}`}>⛔ {b}</li>))}
              </ul>
            </>
          ) : null}
          {deviceWarnings.length > 0 ? (
            <>
              <div className="h-sub" style={{ color: '#9a3412', marginTop: 8 }}>提示项：建议对齐设备库参数</div>
              <ul className="h-list">
                {deviceWarnings.map((w, idx) => (<li key={`dev-warn-${idx}`}>⚠️ {w}</li>))}
              </ul>
            </>
          ) : null}
        </div>
      )}

      {engineeringWarnings.length > 0 && (
        <div className="h-section">
          <div className="h-title">工程校核提示</div>
          <div className="print-hide" style={{ marginBottom: 8 }}>
            <label style={{ marginRight: 8 }}>筛选：</label>
            <select value={warningFilter} onChange={(e) => setWarningFilter(e.target.value)}>
              <option value="all">全部</option>
              <option value="readability">仅可读性</option>
              <option value="nonreadability">排除可读性</option>
            </select>
          </div>
          {(() => {
            const filtered = engineeringWarnings.filter((w) => {
              const isReadable = String(w).includes('【可读性】');
              if (warningFilter === 'readability') return isReadable;
              if (warningFilter === 'nonreadability') return !isReadable;
              return true;
            });
            return (
              filtered.length > 0 ? (
                <ul className="h-list">
                  {filtered.map((w, idx) => (
                    <li key={`warn-${idx}`}>⚠️ {w}</li>
                  ))}
                </ul>
              ) : (
                <div className="h-remark">校核通过，未发现显著风险</div>
              )
            );
          })()}
        </div>
      )}

      {(includeSelectivity && hasSelectivity) && (
        <div className="h-section">
          <div className="h-title">选择性校核</div>
          <div className="h-sub" style={{ marginTop: 4, color: '#666' }}>{t('export.report.selectivity.summary.min_margin')}: {selectivityMatrix.minMargin ?? 0.3}s；{t('export.report.selectivity.summary.overall_ok')}: {selectivityMatrix.overallOk ? t('export.common.yes') : t('export.common.no')}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
          <thead>
            <tr>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'left', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.downstream')}</th>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'left', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.upstream')}</th>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.min_margin')}</th>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.worst_margin')}</th>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.worst_at_i')}</th>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'center', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.pass')}</th>
              <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '6px 8px' }}>{t('export.report.selectivity.table.headers.recommend_tms')}</th>
            </tr>
          </thead>
          <tbody>
            {(selectivityMatrix.pairs || []).map((p, idx) => (
              <tr key={`pair-${idx}`}>
                <td style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 8px' }}>{p.downstream}</td>
                <td style={{ borderBottom: '1px solid #f0f0f0', padding: '6px 8px' }}>{p.upstream}</td>
                <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '6px 8px' }}>{p.minMargin == null ? '—' : String(p.minMargin)}</td>
                <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '6px 8px' }}>{p.worstMargin == null ? '—' : String(p.worstMargin)}</td>
                <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '6px 8px' }}>{p.worstAtI == null ? '—' : String(p.worstAtI)}</td>
                <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'center', padding: '6px 8px' }}>{p.ok ? '是' : '否'}</td>
                <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '6px 8px' }}>{(p.recommendation && typeof p.recommendation.recommended === 'number') ? `${p.recommendation.recommended}${p.recommendation.capped ? t('export.common.capped') : ''}` : '—'}</td>
              </tr>
            ))}
          </tbody>
            </table>
          </div>
          {Array.isArray(selectivityMatrix.adjustments) && selectivityMatrix.adjustments.length > 0 && (
            <div className="h-remark" style={{ marginTop: 6 }}>
              顺序调整建议：{selectivityMatrix.adjustments.map((adj, i) => `${i > 0 ? '； ' : ''}上级“${adj.label}” TMS：${adj.from} → ${adj.to}${adj.capped ? t('export.common.capped') : ''}（最小裕度 ${adj.minMargin}s）`)}
            </div>
          )}
        </div>
      )}

      {results?.curvePoints && Array.isArray(results.curvePoints) && results.curvePoints.length > 0 && (
        <div className="h-section">
          <div className="h-title">反时限曲线</div>
          <CurveChartMini points={results.curvePoints} />
        </div>
      )}

      {type === 'distance' && Array.isArray(results?.zones) && results.zones.length > 0 && (
        <div className="h-section">
          <div className="h-title">阻抗平面</div>
          <ZPlaneMini zones={results.zones} lineAngleDeg={results.lineAngleDeg} />
        </div>
      )}

          {type === 'differential' && results?.characteristic && (
        <div className="h-section">
          <div className="h-title">差动特性</div>
          <div className="h-sub" style={{ marginTop: 4, color: '#666' }}>坐标系: Id–Ir</div>
          <IdIrMini characteristic={results.characteristic} kb={typeof results.secondHarmonicBlocking === 'number' ? results.secondHarmonicBlocking : null} />
          {Array.isArray(results?.characteristic?.segments) && results.characteristic.segments.length > 0 && (
            <div className="h-sub" style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>特性分段</span>
                <label className="print-hide" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 400 }}>
                  <input type="checkbox" checked={includeProjectLine} onChange={(e) => setIncludeProjectLine(e.target.checked)} />
                  包含项目/线路
                </label>
                <button
                  className="print-hide"
                  style={{ padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}
                  onClick={() => {
                    try {
                      const segs = results.characteristic.segments || [];
                      const nowStr = new Date().toLocaleString('zh-CN');
                      const headerLines = [
                        '差动特性分段备注',
                        `名称：${name && name.trim() ? name.trim() : '未命名'}`,
                        `类型：${typeName}`,
                      ];
                      if (includeProjectLine) {
                        const project = (extras?.project || '').trim();
                        const line = (extras?.line || '').trim();
                        if (project) headerLines.push(`项目：${project}`);
                        if (line) headerLines.push(`线路：${line}`);
                      }
                      headerLines.push(`时间：${nowStr}`);
                      const header = headerLines.join('\n');
                      const lines = segs.map((seg, idx) => {
                        const label = seg.label || `SEG${idx + 1}`;
                        const k = typeof seg.slope === 'number' && isFinite(seg.slope) ? seg.slope : '-';
                        const th = seg.threshold != null && isFinite(seg.threshold) ? seg.threshold : '∞';
                        const note = (seg.note || '').trim();
                        return `段${idx + 1}（${label}）：K=${k}，Ir=${th}${note ? `；备注：${note}` : ''}`;
                      });
                      const text = [header, '', ...lines].join('\n');
                      navigator.clipboard.writeText(text).then(() => {
                        alert('分段备注已复制到剪贴板');
                      }).catch(() => {
                        alert('复制失败，请重试');
                      });
                    } catch (e) {
                      alert('复制失败，请重试');
                    }
                  }}
                >复制备注</button>
              </div>
              <ul className="h-list">
                {results.characteristic.segments.map((seg, idx) => {
                  const label = seg.label || `SEG${idx + 1}`;
                  const k = typeof seg.slope === 'number' && isFinite(seg.slope) ? seg.slope.toFixed(2) : '-';
                  const th = seg.threshold != null && isFinite(seg.threshold) ? seg.threshold.toFixed(3) : '∞';
                  const note = (seg.note || '').trim();
                  return (
                    <li key={`seg-${idx}`}>
                      <strong>{label}</strong>: 斜率K={k}； 阈值Ir={th}A{note ? `； 备注：${note}` : ''}
                    </li>
                  );
                })}
              </ul>
              {(() => {
                const segs = results.characteristic.segments || [];
                const noteCount = segs.filter(s => (s.note || '').trim()).length;
                const over120 = segs.filter(s => (s.note || '').trim().length > 120).length;
                const totalChars = segs.reduce((sum, s) => sum + ((s.note || '').trim().length), 0);
                return (
                  <div className="h-remark" style={{ marginTop: 6 }}>
                    备注段数：{noteCount}；超过120字符：{over120}；总字符数：{totalChars}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {barData.length > 0 && (
        <div className="h-section">
          <div className="h-title">条形图</div>
          <BarChartMini data={barData} />
        </div>
      )}

      {extras.comments && extras.comments.trim() && (
        <div className="h-section">
          <div className="h-title">批注</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{extras.comments}</div>
        </div>
      )}

      <div className="h-section">
        <div className="h-title">计算公式</div>
        <ul className="h-list">
          {formulas.map((f, idx) => (<li key={idx}>{f}</li>))}
        </ul>
      </div>
    </div>
  );
};

export default ExportReport;